import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Shield, Clock, BadgeCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";

const SignIn = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleAuthChange(session);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session);
      if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        await handleAuthChange(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthChange = async (session: any) => {
    try {
      console.log("Handling auth change for session:", session);
      // Get user profile to determine role
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        toast({
          title: "Error",
          description: "Could not retrieve user profile. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (profile?.role) {
        // Update user metadata with role
        await supabase.auth.updateUser({
          data: { role: profile.role }
        });

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });

        // Redirect based on role
        switch (profile.role) {
          case 'borrower':
            navigate('/borrower');
            break;
          case 'lender':
            navigate('/lender');
            break;
          case 'vendor':
            navigate('/vendor');
            break;
          case 'admin':
            navigate('/admin');
            break;
          default:
            console.error('Invalid role:', profile.role);
            toast({
              title: "Error",
              description: "Invalid user role. Please contact support.",
              variant: "destructive",
            });
        }
      } else {
        toast({
          title: "Error",
          description: "User role not found. Please contact support.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error during sign in:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center">
      <div className="container mx-auto px-4 py-8 flex flex-col lg:flex-row items-center justify-center gap-12">
        {/* Left side content */}
        <div className="lg:w-1/3 space-y-8">
          <div className="text-center lg:text-left">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Welcome Back
            </h1>
            <p className="text-lg text-gray-600">
              Access your account securely and manage your financial journey with ease.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <Shield className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold text-lg mb-2">Secure Access</h3>
              <p className="text-gray-600">Bank-grade security for your peace of mind</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <Clock className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold text-lg mb-2">Quick Login</h3>
              <p className="text-gray-600">Access your account in seconds</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <BadgeCheck className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold text-lg mb-2">Trusted Platform</h3>
              <p className="text-gray-600">Join thousands of satisfied users</p>
            </div>
          </div>
        </div>

        {/* Right side auth form */}
        <div className="lg:w-1/3 w-full max-w-md">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <LogIn className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
                <p className="text-sm text-gray-600">Welcome back to your account</p>
              </div>
            </div>

            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: '#9b87f5',
                      brandAccent: '#7E69AB',
                    },
                  },
                },
                className: {
                  container: 'w-full',
                  button: 'w-full px-4 py-2 rounded-lg',
                  input: 'rounded-lg',
                  label: 'text-gray-700',
                },
              }}
              view="sign_in"
              providers={[]}
              redirectTo={`${window.location.origin}/signin`}
            />
            
            <div className="mt-6 text-center space-y-4">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Create Account
                </Link>
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/lender/signin" className="text-sm text-gray-600 hover:text-primary">
                  Lender Sign In
                </Link>
                <Link to="/vendor/signin" className="text-sm text-gray-600 hover:text-primary">
                  Vendor Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;